import React, { createContext, useCallback, useContext, useState } from "react";
import { PhaseTransitionAnimation } from "shared/components/common/PhaseChangeAnimation";

const ANIMATION_MAX_MS = 4500;

interface PhaseAnimationContextValue {
  triggerPhaseAnimation: (phaseLabel: string | null) => void;
  triggerFailureAnimation: () => void;
  hidePhaseAnimation: () => void;
}

const PhaseAnimationContext = createContext<PhaseAnimationContextValue | null>(
  null,
);

export function PhaseAnimationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [show, setShow] = useState(false);
  const [phaseLabel, setPhaseLabel] = useState<string | null>(null);
  const [isFailure, setIsFailure] = useState(false);

  const triggerPhaseAnimation = useCallback((label: string | null) => {
    setPhaseLabel(label);
    setIsFailure(false);
    setShow(true);
  }, []);

  const triggerFailureAnimation = useCallback(() => {
    setPhaseLabel(null);
    setIsFailure(true);
    setShow(true);
  }, []);

  const hidePhaseAnimation = useCallback(() => {
    setShow(false);
    setPhaseLabel(null);
    setIsFailure(false);
  }, []);

  const handleComplete = useCallback(() => {
    setShow(false);
    setPhaseLabel(null);
    setIsFailure(false);
  }, []);

  return (
    <PhaseAnimationContext.Provider
      value={{
        triggerPhaseAnimation,
        triggerFailureAnimation,
        hidePhaseAnimation,
      }}
    >
      {children}
      <PhaseTransitionAnimation
        show={show}
        phaseLabel={phaseLabel}
        isFailure={isFailure}
        onComplete={handleComplete}
      />
    </PhaseAnimationContext.Provider>
  );
}

export function usePhaseAnimation(): PhaseAnimationContextValue {
  const ctx = useContext(PhaseAnimationContext);
  if (!ctx) {
    return {
      triggerPhaseAnimation: () => {},
      triggerFailureAnimation: () => {},
      hidePhaseAnimation: () => {},
    };
  }
  return ctx;
}

export { ANIMATION_MAX_MS };
